/**
 * navigation — deklarativer Nav-Baum als Single Source of Truth (SSOT) für die
 * Dashboard-Navigation (Desktop-Dropdowns + Mobile-Menü).
 *
 * M-UX-1.1 (`architecture-ux-restructure.md` §4.1): dieses Modul liefert NUR die
 * DATEN + reinen Prädikate. Es rendert nichts selbst — der Renderer
 * (`components/layout/NavTreeMenus.tsx`, M-UX-1.2) konsumiert `NAV_TREE`/
 * `NAV_ACTIONS` und ersetzt das heutige Hand-Markup in `MainLayout.tsx`.
 * `MainLayout.tsx` ist in DIESEM Paket bewusst UNVERÄNDERT — kein Render-Delta.
 *
 * Design-Entscheidungen (§4.1):
 * 1. Prädikate sind PURE Funktionen über `NavContext` — keine Hooks im Config.
 *    Alle Gates rufen die BESTEHENDEN `canUseX`/`isLoopBBeta`-Funktionen 1:1 auf
 *    (kein Reimplementieren, fail-closed bleibt unangetastet).
 * 2. Badges sind deklarative IDs (`NavBadgeId`), keine Funktionen — der Renderer
 *    mappt `badgeId → Badge-Komponente`, weil Firestore-`count()`-Hooks nur
 *    mounten dürfen, wenn der Eintrag tatsächlich rendert (Rules-of-Hooks,
 *    Muster `GlobalUnreadBadge` in `MainLayout.tsx`).
 * 3. Env-Flags werden in diesem Modul zur Ladezeit gelesen (bestehendes Muster
 *    `App.tsx`/`MainLayout.tsx`) — kein zweiter Flag-Lesepfad.
 *
 * Scope-Hinweis (W1, dokumentiert statt stillschweigend gelöst):
 * - `messages` (`/dashboard/nachrichten`) ist weiterhin ein bare Top-Bar-Link
 *   in `MainLayout.tsx` (kein Dropdown, keine Sektion) und bleibt laut
 *   Architektur (§2.1 Top-Bar-Zeile) dauerhaft außerhalb des Baums — dafür
 *   taucht er in der `ROUTES_WITHOUT_NAV`-Allowlist
 *   (`routes/dashboardRoutePaths.ts`) auf.
 * - M-UX-3.2 (`plan-ux-3.2-advisor.md`, „Advisor-Baum"): `advisorCockpit`
 *   (`/dashboard/cockpit`) und `advisorDatev` (`/dashboard/datev-sammelexport`)
 *   sind hier in `NAV_TREE` verdrahtet (Sektionen `kanzlei`/`pruefung`, hinter
 *   `UX_SHELL_ENABLED`) — die früheren bare Top-Bar-Links in `MainLayout.tsx`
 *   sind bei Flag ON unterdrückt (`!uxShellEnabled &&`-Vorbedingung), bei Flag
 *   OFF unverändert (Null-Delta). Das `mandanten`-Item (§13.3) folgt erst in
 *   M-UX-7.2.
 * - `logout` (Button, kein Routen-Ziel) und die „Abonnement verwalten"-Aktion
 *   (Desktop-Button `handleManageSubscription`: verzweigt in einen externen
 *   Stripe-Portal-Redirect ODER `navigate('/dashboard/abonnement')` — kein
 *   einzelnes statisches Routen-Ziel) sind keine NavItems. Einzig der heute
 *   NUR mobile Upgrade-Anker (`!isPremium` → Link `/dashboard/abonnement`,
 *   architecture §2.1 Fußzeile „Upgrade-Karte … heute nur mobil") wird als
 *   `subscriptionUpgrade`-Item geführt.
 *
 * @module config/navigation
 */

import type { ComponentType } from 'react';
import {
  IconInvoice,
  IconOffer,
  IconClients,
  IconProducts,
  IconWorkflow,
  IconUser,
  IconNew,
  IconFileDownload,
  IconOpenItems,
  IconExpense,
  IconRefresh,
  IconCsv,
  IconVat,
  IconGobd,
  IconScan,
  IconAdvisor,
  IconInbox,
  IconBank,
  IconTheme,
  IconBilling,
  IconPortal,
  IconWorklist,
  IconOverview,
  IconTravel,
} from '../icons';
import { canUseReceiptScan, RECEIPT_SCAN_FLAG } from './receiptScanAccess';
import { canUseTravelExpenses, TRAVEL_EXPENSES_FLAG } from './travelExpensesAccess';
import { canUseDatevExport, DATEV_EXPORT_FLAG } from './datevExportAccess';
import { canUseBankReconcile, BANK_RECONCILE_FLAG } from './bankReconcileAccess';
import { canInviteAdvisor } from './advisorInviteAccess';
import {
  canUseClientInvite,
  canUseKanzleiCockpit,
  canUseAdvisorDatev,
  isKanzleiAccount,
} from './kanzleiAccess';
import { canUseInbound } from './inboundAccess';
import { canUseUnifiedInbox } from './unifiedInboxAccess';
import { INVOICE_FORM_ROUTE, canUseCockpit } from './cockpitAccess';
import { ADVISOR_LANDING_ENABLED } from './advisorLandingAccess';
import { canUseOpenItems } from './openItemsAccess';
import { canUseUstva, USTVA_FLAG } from './ustvaAccess';
import { canUseRecurring } from './recurringAccess';
import { canUseGobd } from './gobdAccess';
import { type SellerLike, type Tier } from './entitlements';
import { type NudgePromotionId } from './nudgePromotions';

// Feature flags — fail-closed: nav entries only shown when explicitly enabled.
// Gespiegelt aus `MainLayout.tsx`/`App.tsx` (bestehende Duplikation, kein
// zentraler Export vorhanden — Muster wie `bankReconcileAccess.ts`-Kommentar
// zur Env-Var-Spiegelung begründet).
const EXPENSES_ENABLED = process.env.REACT_APP_EXPENSES_ENABLED === 'true';
// RECURRING_ENABLED/USTVA_ENABLED/GOBD_ENABLED wurden zu eigenen Access-Modulen
// (`config/recurringAccess.ts`/`ustvaAccess.ts`/`gobdAccess.ts`) migriert, damit
// die Beta-UID-Allowlist + (bei UStVA) der Ultra-Tier-Term an EINER Stelle leben
// und identisch von Nav, Route-Registrierung und Seiten-Gate genutzt werden
// (Muster `receiptScanAccess`/`travelExpensesAccess`). Sichtbarkeit unten via
// `canUseRecurring`/`canUseUstva`/`canUseGobd`.
// M-UX-3.2 (`plan-ux-3.2-advisor.md` §1): geteiltes Flag mit M-UX-3.1 — gatet
// die Sichtbarkeit der `kanzlei`/`pruefung`-Sektionen (s. u.). Flag off ⇒ beide
// Sektionen enthalten nur `hidden`-Items ⇒ `resolveVisibleTree` filtert sie
// komplett weg (Null-Delta zum Post-3.1-Stand).
const UX_SHELL_ENABLED = process.env.REACT_APP_UX_SHELL_ENABLED === 'true';

/** Sichtbarkeitszustand eines Nav-Eintrags (§4.1). */
export type NavVisibility = 'visible' | 'locked' | 'hidden';

/** Welcher Rollen-Baum ein Abschnitt bedient (§2.1/§2.2). */
export type NavRole = 'owner' | 'advisor';

/**
 * Deklarative Badge-IDs — der Renderer mappt `badgeId → Badge-Komponente`.
 * W1: nur `messagesUnread` konzeptionell vorgesehen (Item selbst noch nicht in
 * NAV_TREE, s. Modul-Kopf); `inboxNew` kommt ab W5 (Unified Inbox).
 */
export type NavBadgeId = 'messagesUnread' | 'inboxNew';

/**
 * Minimale, strukturell typisierte Sicht auf `sellerData` für die
 * Loop-B-Beta-Gates (`isLoopBBeta`/`canInviteAdvisor`). `NavContext.sellerData`
 * ist bewusst `unknown` (Entkopplung des Config-Moduls vom konkreten
 * `Seller`-Typ aus `types/invoiceTypes`) — dieser Helper ist der EINE Ort, an
 * dem die Laufzeit-Annahme „sellerData ist entweder null/undefined oder ein
 * Objekt mit optionalem `loopBBeta`-Feld" (== das echte `Seller`-Shape aus
 * `AuthContext`) explizit gemacht wird.
 */
function asLoopBBetaSellerData(
  sellerData: unknown,
): { loopBBeta?: boolean } | null | undefined {
  if (sellerData === null || sellerData === undefined) {
    return sellerData;
  }
  return sellerData as { loopBBeta?: boolean };
}

/**
 * Laufzeit-Kontext, über den alle Nav-Prädikate entscheiden (§4.1). Wird vom
 * Renderer (M-UX-1.2) aus den bestehenden Hooks (`useAuth`, `useIsReadOnly`,
 * `useIsAdvisorContext`) zusammengebaut — dieses Modul selbst ruft KEINE Hooks.
 */
export interface NavContext {
  /** `currentUser?.uid` aus `useAuth()`. */
  uid: string | undefined;
  /** `effectiveScopeUid` aus `useAuth()` — der Tenant-SCOPE, nicht die eigene UID. */
  effectiveScopeUid: string;
  /** `isPremium` aus `useAuth()`. */
  isPremium: boolean;
  /**
   * Effektiver Tier des Nutzers (`resolveTier(sellerData)` aus `entitlements.ts`,
   * Pricing-Repackaging §3.2). PFLICHTFELD: erzwingt, dass jeder `NavContext`-
   * Ersteller (`MainLayout.buildNavContext` + Test-Familie) den Tier explizit
   * setzt — so findet der Compiler alle Stellen, an denen die Ultra-Gates
   * (`canUseBankReconcile`/`canUseReceiptScan`/`canUseDatevExport`) über `ctx`
   * ausgewertet werden.
   */
  tier: Tier;
  /** `useIsReadOnly()` — `true` NUR im Mandanten-Modus (Fremd-Tenant, read-only). */
  isAdvisorViewingClient: boolean;
  /** `useIsAdvisorContext()` — `true` auch im eigenen Kanzlei-Kontext. */
  isAdvisorContext: boolean;
  /** `sellerData` aus `useAuth()` (oder `null`, solange nicht geladen). */
  sellerData: unknown | null;
}

/** Ein einzelner Nav-Eintrag (Link-Ziel innerhalb einer Sektion). */
export interface NavItem {
  /** Stabil — Tests, Telemetrie, E2E-Selektoren (`data-nav-id`, ab M-UX-1.2). */
  id: string;
  /** i18n-Key, Namespace `'navigation'`. */
  labelKey: string;
  /** Absoluter Pfad unter `/dashboard`. */
  route: string;
  icon: ComponentType;
  visibility: (ctx: NavContext) => NavVisibility;
  badge?: NavBadgeId;
  /** `true` ⇒ aktiv nur bei exaktem Pfad-Match (Fix Z2, s. §4.1). Default: Prefix-Match. */
  activeExact?: boolean;
  /**
   * Additives, ctx-fähiges Alias-Prädikat (architecture-cockpit-route-split.md
   * §5.4, M-CRS-4): zusätzliche Bedingung, unter der das Item als aktiv gilt —
   * ergänzt das Pfad-Match (`route`/`activeExact`), ersetzt es nicht. Default
   * `undefined` ⇒ Null-Delta für alle bestehenden Items. Nötig, weil
   * `INVOICE_FORM_ROUTE` jetzt konstant auf `/dashboard/rechnungen/neu` zeigt,
   * ein normaler Nutzer nach Login aber auf `/dashboard` (Formular) landet und
   * `createInvoice` dort sonst das Highlight verlöre.
   */
  activeAlsoWhen?: (pathname: string, ctx: NavContext) => boolean;
  /**
   * Nudge-Fläche, die dieses Item im `locked`-Zustand bewirbt (F-2, Re-Loop 2).
   *
   * PFLICHT für jedes Item, dessen `visibility` `'locked'` zurückgeben kann —
   * der Renderer (`NavTreeMenus`) leitet daraus den Tarif-Namen für
   * `title`/`aria-label` des Schloss-Eintrags ab, über
   * `getPromotionTargetTier(id)`. Damit gibt es für die Frage „welcher Tarif
   * gilt für dieses Feature?" weiterhin GENAU EINE Quelle
   * (`NUDGE_PROMOTION_TARGET_TIER`), die dieselbe ist, aus der auch der
   * `?tier=`-Parameter der Upgrade-Hrefs kommt (D-1).
   *
   * Vorher baute `NavTreeMenus` den Hinweis als hartkodiertes
   * `` `${label} – Premium` `` — für die Ultra-Items (Belegscanner, Bank,
   * DATEV und die zwei in diesem PR neu erzeugten `locked`-Zustände
   * Reisekosten/UStVA) war das eine falsche Tarif-Zuordnung im Tooltip UND im
   * Screenreader-Label.
   *
   * Optional im Typ, weil `visible`/`hidden`-only-Items keine Nudge-Fläche
   * haben; fehlt der Wert an einem lockable Item, fällt der Renderer auf einen
   * tarifNEUTRALEN Text zurück (nie auf einen geratenen Tarif).
   */
  lockedPromotionId?: NudgePromotionId;
}

/** Eine Nav-Sektion (Dropdown/Sidebar-Gruppe). */
export interface NavSection {
  id: string;
  /** i18n-Key, Namespace `'navigation'`. */
  labelKey: string;
  /** Welche(r) Rollen-Baum diese Sektion sieht (ersetzt verstreute `!isAdvisorViewingClient`-Checks auf Sektionsebene). */
  roles: NavRole[];
  items: NavItem[];
  /**
   * Additiv/optional (M-UX-Cockpit-Home, `plan-ux-table-cockpit-quality.md`
   * §D.2): unterdrückt die Sektions-Überschrift (`<h2>`) im persistenten
   * `SidebarNav`. Gedacht für Ein-Item-Sektionen, deren Überschrift das Item
   * nur doppeln würde (z. B. „Übersicht"). NUR `SidebarNav` beachtet dieses
   * Feld — `MobileNavGroup`/`DesktopNavDropdown` ignorieren es schadlos
   * (Default `undefined` ⇒ heutiges Verhalten, Null-Delta für alle
   * bestehenden Sektionen).
   */
  chromeless?: boolean;
}

/** Ein Eintrag im „+ Neu"-Schnellerstellungs-Menü. */
export interface NavAction {
  id: string;
  labelKey: string;
  route: string;
  icon: ComponentType;
  visibility: (ctx: NavContext) => NavVisibility;
}

/**
 * Belegscanner-Sichtbarkeit (§4.1 „locked-Regel" — PR-T6/D7 generalisiert die
 * Regel zusätzlich auf `bankReconcile`/`datevExport`, die Ultra-Upsell-Konsistenz
 * 2026-07-29 zusätzlich auf `travelExpenses`/`umsatzsteuer`, s.
 * `resolveBankReconcileVisibility`/`resolveDatevExportVisibility`/
 * `resolveTravelExpensesVisibility`/`resolveUstvaVisibility` unten; `openItems`
 * trägt sie inline über
 * `canUseOpenItems`). `locked` NUR wenn das allgemeine Flag aktiv ist, der Nutzer aber kein Premium
 * hat und NICHT über die Beta-Allowlist bereits Zugriff hat (`canUseReceiptScan`
 * deckt Beta-Allowlist ODER Flag+Premium ab — wer hier `locked` erreicht, ist
 * also nachweislich nicht auf der Allowlist). Advisor im Mandanten-Modus sieht
 * den Eintrag unverändert (heutiges Verhalten, `MainLayout.tsx` `|| isAdvisorViewingClient`).
 *
 * M-UX-2.1 (Unified-Inbox-Konsolidierung, `plan-ux-2.1-nav-sektionen.md` §2b):
 * die additive Koexistenz aus M-UX-5.2 ENDET hier. Sobald der Nutzer die
 * Unified Inbox sehen darf (`canUseUnifiedInbox` — Zielgruppen-Gate statt
 * globalem Flag, s. Fix 2026-07-14), lebt der Scan-Strom in der
 * Unified-Inbox-Seite selbst (Teaser-/`locked`-Conversion wandert dorthin,
 * §6.5 flip-ready) — der Nav-Eintrag wird deshalb als ERSTE Prüfung
 * ausgeblendet. Für Nutzer ohne Unified-Inbox-Zugriff ist dieser Zweig ein
 * Null-Delta gegenüber M-UX-5.2 (alle folgenden Gates unverändert).
 */
function resolveReceiptScanVisibility(ctx: NavContext): NavVisibility {
  if (canUseUnifiedInbox(ctx.effectiveScopeUid, ctx.tier)) return 'hidden';
  if (canUseReceiptScan(ctx.uid, ctx.tier)) return 'visible';
  if (ctx.isAdvisorViewingClient) return 'visible';
  // Locked-/Upsell-Regel (§3.2): Flag an, aber kein Ultra-Tier (und nicht via
  // Beta-Allowlist bereits `visible`) ⇒ `locked`. Ersetzt den früheren
  // `!isPremium`-Term (Belegscanner ist ab GA ein Ultra-Feature).
  if (RECEIPT_SCAN_FLAG && ctx.tier !== 'ultra') return 'locked';
  return 'hidden';
}

/**
 * Bank-Abgleich-Sichtbarkeit (PR-T6, D7: §3.2 „locked"-Regel generalisiert
 * über receiptScan hinaus). `locked` NUR wenn das allgemeine Flag aktiv ist,
 * der Nutzer aber kein Ultra hat und NICHT über die Beta-Allowlist bereits
 * Zugriff hat (`canUseBankReconcile` deckt Beta-Allowlist ODER Flag+Ultra ab —
 * wer hier `locked` erreicht, ist also nachweislich nicht auf der Allowlist).
 * Muster identisch zu `resolveReceiptScanVisibility`. Bei Flag aus bleibt der
 * Zweig unerreichbar ⇒ byte-identisch zum bisherigen `'hidden'`.
 */
function resolveBankReconcileVisibility(ctx: NavContext): NavVisibility {
  if (canUseBankReconcile(ctx.uid, ctx.tier)) return 'visible';
  if (BANK_RECONCILE_FLAG) return 'locked';
  return 'hidden';
}

/**
 * DATEV-Export-Sichtbarkeit (PR-T6, D7: §3.2 „locked"-Regel generalisiert).
 * `locked` NUR im OWNER-Kontext bei aktivem Flag und Nicht-Ultra-Tier — der
 * T4b-Advisor-Bypass (`isAdvisorViewingClient`) bleibt davon unberührt: ein
 * StB erreicht diesen Zweig NIE als `locked`, weil `canUseDatevExport` im
 * Advisor-Kontext bereits bei reinem Flag-an `true` liefert (kein
 * Owner-Tier-Term, §3.2b) — die einzige Nicht-Berechtigt-Situation für einen
 * Advisor ist Flag AUS, und dort bleibt der Eintrag `hidden` (kein Kaufanreiz
 * für eine Beta-Erprobung). Die explizite `!ctx.isAdvisorViewingClient`-
 * Bedingung ist defense-in-depth, kein Verhaltensdelta.
 */
function resolveDatevExportVisibility(ctx: NavContext): NavVisibility {
  if (canUseDatevExport(ctx.effectiveScopeUid, ctx.tier, ctx.isAdvisorViewingClient)) return 'visible';
  if (DATEV_EXPORT_FLAG && !ctx.isAdvisorViewingClient) return 'locked';
  return 'hidden';
}

/**
 * Reisekosten-Sichtbarkeit (Ultra-Upsell-Konsistenz 2026-07-29): dieselbe
 * `locked`-Regel wie `resolveBankReconcileVisibility`/`resolveDatevExportVisibility`
 * (§3.2, PR-T6/D7), jetzt auch für Reisekosten — bis hierher war der Eintrag für
 * Nicht-Ultra-Nutzer schlicht `'hidden'`, d. h. das Feature, das Ultra verkaufen
 * soll, war für die Zielgruppe unsichtbar („ein unsichtbarer Tier verkauft sich
 * nicht", ux-audit-growth.md Befund #4).
 *
 * `locked` NUR wenn das allgemeine Flag aktiv ist (Feature ist ausgeliefert) und
 * der Nutzer nicht bereits über die Beta-Allowlist ODER Flag+Ultra berechtigt ist
 * (`canUseTravelExpenses`). Bei Flag AUS bleibt es bei `'hidden'` — kein
 * Kaufanreiz für eine reine Beta-Erprobung (Muster
 * `BankReconciliationPage`-Kommentar). Der `!ctx.isAdvisorViewingClient`-Term
 * hält die Paywall aus dem Mandanten-Modus heraus (StB nie mit dem Tier des
 * Mandanten konfrontieren, T6-Auflage 3).
 */
function resolveTravelExpensesVisibility(ctx: NavContext): NavVisibility {
  if (canUseTravelExpenses(ctx.uid, ctx.tier)) return 'visible';
  if (TRAVEL_EXPENSES_FLAG && !ctx.isAdvisorViewingClient) return 'locked';
  return 'hidden';
}

/**
 * UStVA-Sichtbarkeit (Ultra-Upsell-Konsistenz 2026-07-29): Muster
 * `resolveTravelExpensesVisibility`. Der `visible`-Zweig behält die bisherige
 * Semantik EXAKT bei (`canUseUstva(uid, tier) && !isAdvisorViewingClient` —
 * die UStVA-Sicht ist owner-exklusiv, §2.1). Neu ist ausschließlich der
 * `locked`-Zweig: Flag an, Owner-Kontext, aber kein Ultra ⇒ Upsell statt
 * unsichtbarem Eintrag. Advisor im Mandanten-Modus bleibt `hidden` (weder
 * Inhalt noch Paywall), Flag aus bleibt `hidden`.
 */
function resolveUstvaVisibility(ctx: NavContext): NavVisibility {
  if (canUseUstva(ctx.uid, ctx.tier) && !ctx.isAdvisorViewingClient) return 'visible';
  if (USTVA_FLAG && !ctx.isAdvisorViewingClient) return 'locked';
  return 'hidden';
}

/**
 * Deklarativer Nav-Baum — deckt 100 % der heutigen Desktop-/Mobile-Nav-Einträge
 * ab (Ausnahmen s. Modul-Kopf: `messages`/`advisorCockpit`/`advisorDatev`/
 * `logout`/Abo-Verwalten-Button). Beta-Features bleiben `hidden` (Flip-Ready,
 * §1.1) — die zugrundeliegenden `canUseX`-Gates sind 1:1 aus dem Bestand
 * übernommen, nicht neu erfunden.
 */
export const NAV_TREE: NavSection[] = [
  {
    // M-UX-Cockpit-Home (`plan-ux-table-cockpit-quality.md` §D.2): „Übersicht"
    // als OBERSTER Sidebar-Eintrag (vor `actions` ⇒ landet als erste Sektion in
    // `midSections`), damit das Cockpit-Home nach dem Wegnavigieren wieder
    // erreichbar ist. Cockpit-Flag-gekoppelt: bei Flag OFF ist das einzige Item
    // `hidden` ⇒ `resolveVisibleTree` filtert die (dann leere) Sektion komplett
    // weg ⇒ Null-Delta. `chromeless` unterdrückt die redundante „ÜBERSICHT"-
    // Überschrift über dem gleichnamigen Ein-Item.
    id: 'uebersicht',
    labelKey: 'uebersicht',
    roles: ['owner'],
    chromeless: true,
    items: [
      {
        id: 'overview',
        labelKey: 'overview',
        route: '/dashboard',
        icon: IconOverview,
        // PFLICHT: Exakt-Match — ein Prefix-Match würde auf JEDER /dashboard/*-
        // Route highlighten (§D.2).
        activeExact: true,
        // architecture-cockpit-route-split.md §5.3: uid-abhängig statt rein
        // flag-abhängig — Beta-UIDs sehen „Übersicht" ab sofort, normale Nutzer
        // erst beim GA-Flip. `ctx.uid` ist bereits `currentUser?.uid`
        // (NavContext), konsistent zum Index-Entscheid `DashboardIndexRoute`.
        visibility: (ctx) => (canUseCockpit(ctx.uid) ? 'visible' : 'hidden'),
      },
    ],
  },
  {
    id: 'actions',
    labelKey: 'new',
    // Heute: gesamter „Neu"-Dropdown hinter `!isReadOnly` (== !isAdvisorViewingClient),
    // MainLayout.tsx:371/746.
    roles: ['owner'],
    items: [
      {
        id: 'createInvoice',
        labelKey: 'createInvoice',
        // architecture-cockpit-route-split.md §3 E2: konstantes Formular-Ziel
        // `/dashboard/rechnungen/neu` (flag-frei) — nie `/dashboard`, daher kein
        // Self-Loop.
        route: INVOICE_FORM_ROUTE,
        icon: IconInvoice,
        visibility: () => 'visible',
        activeExact: true,
        // §5.4 (M-CRS-4): ein normaler Nutzer (kein Cockpit) landet nach Login
        // auf `/dashboard` (Formular). Ohne dieses Alias verlöre `createInvoice`
        // dort das heutige Highlight (Route zeigt jetzt auf `/rechnungen/neu`).
        // Für Cockpit-Nutzer ist `canUseCockpit(ctx.uid)===true` ⇒ Alias inaktiv
        // (kein Doppel-Highlight; `/dashboard` highlightet dann nur `overview`).
        activeAlsoWhen: (pathname, ctx) => pathname === '/dashboard' && !canUseCockpit(ctx.uid),
      },
      {
        id: 'createOffer',
        labelKey: 'createOffer',
        route: '/dashboard/angebote',
        icon: IconOffer,
        visibility: () => 'visible',
      },
    ],
  },
  {
    id: 'pruefung',
    labelKey: 'pruefung',
    // M-UX-3.2 (`plan-ux-3.2-advisor.md` §2.1/§3): Mandanten-Modus-Landing-
    // Anker — advisor-exklusiv (`roles:['advisor']`), erscheint also nur im
    // Mandanten-Modus (isAdvisorViewingClient), nicht im eigenen
    // Kanzlei-Kontext (dort roles=['owner'] greift, s. `kanzlei` unten).
    roles: ['advisor'],
    items: [
      {
        // Distinkte Item-ID (≠ `advisorCockpit`), gleiche Route
        // `/dashboard/cockpit` — zulässig, der Registry-Test prüft nur
        // Routen-Registrierung, keine ID-Unizität (§2.1 Fußnote).
        id: 'cockpitWorklist',
        // reused Label „Cockpit" (kein neuer i18n-Key nötig, §6).
        labelKey: 'advisorCockpit',
        route: '/dashboard/cockpit',
        icon: IconWorklist,
        visibility: (ctx) =>
          UX_SHELL_ENABLED && canUseKanzleiCockpit(ctx.uid, asLoopBBetaSellerData(ctx.sellerData))
            ? 'visible'
            : 'hidden',
      },
    ],
  },
  {
    id: 'verkauf',
    labelKey: 'verkauf',
    // M-UX-2.1 (`plan-ux-2.1-nav-sektionen.md` §1.2): Verkaufs-Dokumente
    // (Rechnungen/Angebote/Workflows/Wiederkehrend), zuvor Teil der
    // „Dokumente"-Grabbelkiste. Item-Gates 1:1 unverändert.
    roles: ['owner', 'advisor'],
    items: [
      {
        id: 'invoices',
        labelKey: 'invoices',
        route: '/dashboard/archiv',
        icon: IconInvoice,
        visibility: () => 'visible',
      },
      {
        id: 'offers',
        labelKey: 'offers',
        route: '/dashboard/angebote-archiv',
        icon: IconOffer,
        visibility: (ctx) => (ctx.isAdvisorViewingClient ? 'hidden' : 'visible'),
      },
      {
        id: 'recurringInvoices',
        labelKey: 'recurringInvoices',
        route: '/dashboard/wiederkehrend',
        icon: IconRefresh,
        // Beta-Unlock: Sichtbarkeit 1:1 über den geteilten Seiten-Gate-Helfer
        // `canUseRecurring(ctx.uid)` (Beta-UID ODER Flag, kein Tier-Term).
        visibility: (ctx) =>
          canUseRecurring(ctx.uid) && !ctx.isAdvisorViewingClient ? 'visible' : 'hidden',
      },
      {
        id: 'workflows',
        labelKey: 'workflows',
        route: '/dashboard/workflows',
        icon: IconWorkflow,
        visibility: (ctx) => (ctx.isAdvisorViewingClient ? 'hidden' : 'visible'),
      },
    ],
  },
  {
    id: 'eingaenge',
    labelKey: 'eingaenge',
    // M-UX-2.1 (§1.3): Belege & Eingänge — alle Eingangs-/Beleg-Ströme
    // (inkl. Scanner, s. §2a) an einem Ort. Unified-Inbox-Konsolidierung
    // endet die additive Koexistenz aus M-UX-5.2 (§2b): bei Flag ON werden
    // die drei Alt-Ströme ausgeblendet, weil ihre Routen ohnehin auf
    // `/eingaenge` redirecten (`routes/dashboardChildren.tsx`).
    roles: ['owner', 'advisor'],
    items: [
      {
        id: 'eingaenge',
        // M-UX-2.1: labelKey wechselt `eingaenge` → `inbox` (M-V4-D2: „Eingänge",
        // zuvor „Posteingang"), damit das Item-Label nicht die Sektions-
        // Überschrift „Belege & Eingänge" dupliziert. Die Item-ID bleibt
        // `eingaenge` (E2E-/Test-Stabilität, `navigation.test.ts` Block 11).
        labelKey: 'inbox',
        route: '/dashboard/eingaenge',
        icon: IconInbox,
        // M-UX-5.2: additiver Eintrag — nur sichtbar, wenn der Nutzer die
        // Unified Inbox nutzen darf (Zielgruppen-Gate, s. Fix 2026-07-14).
        visibility: (ctx) => (canUseUnifiedInbox(ctx.effectiveScopeUid, ctx.tier) ? 'visible' : 'hidden'),
        // `badge: 'inboxNew'` deklariert (Hook `hooks/useInboxNewCount.ts`
        // existiert) — die Renderer-Verdrahtung (`badgeRegistry`-Prop in
        // `MainLayout.tsx`) ist bewusst NICHT Teil dieses Pakets (Hot-File-
        // Kollisionsschutz, s. `useInboxNewCount.ts` Kopfkommentar). Ein
        // unverdrahteter `badgeId` ist inert (`NavTreeMenus.tsx`: `item.badge
        // ? badgeRegistry?.[item.badge] : undefined` → kein Badge, kein Fehler).
        badge: 'inboxNew',
      },
      {
        id: 'posteingang',
        labelKey: 'posteingang',
        route: '/dashboard/posteingang',
        icon: IconInbox,
        // M-UX-2.1 (§2b): Unified-Hide VOR dem bestehenden Gate — hat der
        // Nutzer Unified-Inbox-Zugriff, redirected die Route ohnehin auf
        // `/eingaenge`, der Alt-Eintrag wird ausgeblendet. Ohne Zugriff
        // unverändertes Verhalten (Null-Delta).
        visibility: (ctx) =>
          canUseUnifiedInbox(ctx.effectiveScopeUid, ctx.tier)
            ? 'hidden'
            : canUseInbound(ctx.effectiveScopeUid)
              ? 'visible'
              : 'hidden',
      },
      {
        id: 'receivedInvoices',
        labelKey: 'receivedInvoices',
        route: '/dashboard/eingangsrechnungen',
        icon: IconFileDownload,
        // M-UX-2.1 (§2b): additive Koexistenz aus M-UX-5.2 endet — hat der
        // Nutzer Unified-Inbox-Zugriff, wird ausgeblendet (Route redirected
        // ohnehin auf `/eingaenge`).
        visibility: (ctx) => (canUseUnifiedInbox(ctx.effectiveScopeUid, ctx.tier) ? 'hidden' : 'visible'),
      },
      {
        id: 'receiptScan',
        // F-2: Tarif des locked-Tooltips leitet sich hieraus ab (getPromotionTargetTier).
        lockedPromotionId: 'feature_teaser_receipt_scan',
        labelKey: 'receiptScan',
        route: '/dashboard/belege',
        icon: IconScan,
        visibility: resolveReceiptScanVisibility,
      },
      {
        id: 'expenses',
        labelKey: 'expenses',
        route: '/dashboard/ausgaben',
        icon: IconExpense,
        // PR-T6 (§3.3-SONDERFALL, Korrektur von T5): die Ausgaben-/Beleg-LISTE
        // ist frei und bleibt IMMER erreichbar — sie ist KEIN Premium-Feature.
        // NUR die Vorsteuer-Cockpit-Summenleiste innerhalb der Seite ist
        // ultra-gegatet (`ExpensesPage.tsx`, `canUseVorsteuerCockpit`). Ein
        // `'locked'`-Nav-Eintrag würde fälschlich die GESAMTE Seite sperren,
        // obwohl nur die Summenleiste betroffen ist — deshalb NUR noch
        // EXPENSES_ENABLED-gegated (byte-identisch zum ursprünglichen IST vor
        // T5's fälschlicher 'locked'-Verdrahtung).
        visibility: () => (EXPENSES_ENABLED ? 'visible' : 'hidden'),
      },
      {
        id: 'travelExpenses',
        // F-2: Tarif des locked-Tooltips leitet sich hieraus ab (getPromotionTargetTier).
        lockedPromotionId: 'feature_teaser_travel_expenses',
        labelKey: 'travelExpenses',
        route: '/dashboard/reisekosten',
        icon: IconTravel,
        // RK-7 Nav-Ergänzung (Nav-Team): Reisekosten/Dienstreisen-Erfassung. Gated
        // identisch zum Seiten-/Route-Gate (`canUseTravelExpenses`, uid + tier)
        // — Beta-Allowlist ODER Flag+Ultra (GA-Umzug §3). Ultra-Upsell-Konsistenz
        // 2026-07-29: ausgearbeiteter `locked`-Zustand statt reinem `hidden` bei
        // aktivem Flag + Nicht-Ultra (s. `resolveTravelExpensesVisibility`);
        // fail-closed bleibt 'hidden', solange das Flag aus ist.
        visibility: resolveTravelExpensesVisibility,
      },
    ],
  },
  {
    id: 'zahlungen',
    labelKey: 'zahlungen',
    // M-UX-2.1 (§1.4): Zahlungsströme (Offene Posten/Mahnwesen + Bank-Abgleich).
    roles: ['owner', 'advisor'],
    items: [
      {
        id: 'openItems',
        // F-2: Tarif des locked-Tooltips leitet sich hieraus ab (getPromotionTargetTier).
        lockedPromotionId: 'feature_teaser_open_items',
        labelKey: 'openItems',
        route: '/dashboard/offene-posten',
        icon: IconOpenItems,
        // PR-T5: Take-away-Gate Offene Posten. `canUseOpenItems` liefert bei
        // TIER_V2_FLAG=false IMMER true ⇒ 'visible' (byte-identisch zum heutigen
        // ungegateten Eintrag). Der 'locked'-Zweig greift erst bei aktivem
        // Master-Flag für nicht-berechtigte Nutzer; ausgearbeiteter
        // Upsell-Inhalt (FeatureTeaser statt Leer-Placeholder) = PR-T6,
        // `OpenItemsPage.tsx`. M-A1 (§6.3, T4b-Nav-Parität): dritter Kontext
        // `ctx.isAdvisorViewingClient` = StB betrachtet fremden Tenant ⇒ nie
        // durch Mandanten-Tier gesperrt.
        visibility: (ctx) =>
          canUseOpenItems(
            ctx.sellerData as SellerLike | null,
            ctx.isAdvisorViewingClient,
          )
            ? 'visible'
            : 'locked',
      },
      {
        id: 'bankReconcile',
        // F-2: Tarif des locked-Tooltips leitet sich hieraus ab (getPromotionTargetTier).
        lockedPromotionId: 'feature_teaser_bank_reconcile',
        labelKey: 'bankReconcile',
        route: '/dashboard/bank-abgleich',
        icon: IconBank,
        // PR-T6 (D7): Ausgearbeiteter `locked`-Zustand statt reinem `hidden`
        // bei aktivem Flag + Nicht-Ultra (s. `resolveBankReconcileVisibility`).
        visibility: resolveBankReconcileVisibility,
      },
    ],
  },
  {
    id: 'steuer',
    labelKey: 'steuer',
    // M-UX-2.1 (§1.5): Steuer & Export (USt-Voranmeldung, DATEV, GoBD).
    roles: ['owner', 'advisor'],
    items: [
      {
        id: 'umsatzsteuer',
        // F-2: Tarif des locked-Tooltips leitet sich hieraus ab (getPromotionTargetTier).
        lockedPromotionId: 'feature_teaser_ustva',
        labelKey: 'umsatzsteuer',
        route: '/dashboard/umsatzsteuer',
        icon: IconVat,
        // Beta-Unlock: Sichtbarkeit 1:1 über den geteilten Seiten-Gate-Helfer
        // `canUseUstva(ctx.uid, ctx.tier)` — Beta-UID ODER (Flag UND Ultra-Tier).
        // Der Ultra-Term schließt die bisherige Gate-Lücke (Tabelle verkaufte
        // UStVA als Ultra, Code hatte kein Tier-Gate). Ultra-Upsell-Konsistenz
        // 2026-07-29: ausgearbeiteter `locked`-Zustand für den Owner-Zweig statt
        // reinem `hidden` (s. `resolveUstvaVisibility`).
        visibility: resolveUstvaVisibility,
      },
      {
        id: 'datevExport',
        // F-2: Tarif des locked-Tooltips leitet sich hieraus ab (getPromotionTargetTier).
        lockedPromotionId: 'feature_teaser_datev_export',
        labelKey: 'datevExport',
        route: '/dashboard/datev-export',
        icon: IconCsv,
        // T4b (§3.2b-FIX): 3-Param-Vertrag wie Belege/Inbound. Bewusst
        // `ctx.effectiveScopeUid` statt `ctx.uid` (Gate-Kohärenz mit Seiten-Gate +
        // Beta-Tenant-Semantik der Allowlist; für Owner identisch). Dritter Arg
        // `ctx.isAdvisorViewingClient` = StB betrachtet fremden Tenant ⇒ Advisor
        // wird nie durch Mandanten-Tier gesperrt. PR-T6 (D7): Ausgearbeiteter
        // `locked`-Zustand für den Owner-Zweig statt reinem `hidden`
        // (s. `resolveDatevExportVisibility`, Advisor-Bypass bleibt erhalten).
        visibility: resolveDatevExportVisibility,
      },
      {
        id: 'gobdVerfahrensdoku',
        labelKey: 'gobdVerfahrensdoku',
        route: '/dashboard/gobd-export',
        icon: IconGobd,
        // Beta-Unlock (Jurist-Freigabe 2026-07-17, Beta-only): Sichtbarkeit 1:1
        // über `canUseGobd(ctx.uid)` (Beta-UID ODER Flag, kein Tier-Term).
        visibility: (ctx) =>
          canUseGobd(ctx.uid) && !ctx.isAdvisorViewingClient ? 'visible' : 'hidden',
      },
      {
        id: 'gobdDoku',
        labelKey: 'gobdDoku',
        route: '/dashboard/gobd',
        icon: IconGobd,
        // GoBD-Doku-Volltext-Seite — teilt sich das Gate 1:1 mit `gobdVerfahrensdoku`
        // (Export-Panel): dieselbe `canUseGobd(ctx.uid)`-Beta-/Flag-Bedingung, kein
        // eigenes Flag (config/gobdAccess.ts).
        visibility: (ctx) =>
          canUseGobd(ctx.uid) && !ctx.isAdvisorViewingClient ? 'visible' : 'hidden',
      },
    ],
  },
  {
    id: 'stammdaten',
    labelKey: 'stammdaten',
    // M-UX-2.1 (§1.6): heute „Verwaltung" — Kunden/Produkte-Stammdaten.
    // Heute: gesamter Dropdown hinter `!isAdvisorViewingClient`,
    // MainLayout.tsx:543/888 (IST vor diesem Paket).
    roles: ['owner'],
    items: [
      {
        id: 'customers',
        labelKey: 'customers',
        route: '/dashboard/kunden',
        icon: IconClients,
        visibility: () => 'visible',
      },
      {
        id: 'products',
        labelKey: 'products',
        route: '/dashboard/produkte',
        icon: IconProducts,
        visibility: () => 'visible',
      },
    ],
  },
  {
    id: 'steuerberater',
    labelKey: 'steuerberater',
    // M-UX-2.1 (§1.7/§2c): aus dem Konto/Avatar-Menü herausgeholt — „Als
    // sichtbare Sektion", MINIMAL ohne M-UX-3.2 (Advisor-„kanzlei"-Baum)
    // vorzuziehen. Item-Gates 1:1 unverändert, nur an neuem Ort.
    roles: ['owner', 'advisor'],
    items: [
      {
        id: 'advisorPortal',
        labelKey: 'advisorPortal',
        route: '/dashboard/konto/steuerberater',
        icon: IconAdvisor,
        visibility: (ctx) =>
          canInviteAdvisor(ctx.uid, asLoopBBetaSellerData(ctx.sellerData)) || ctx.isAdvisorContext
            ? 'visible'
            : 'hidden',
      },
      {
        id: 'clientInvitePortal',
        labelKey: 'clientInvitePortal',
        route: '/dashboard/konto/mandanten-einladen',
        icon: IconClients,
        // M-OB-12 (§8.3): additiv um die W3-Self-Service-Kennzeichnung erweitert
        // — eine frische Kanzlei (accountType==='kanzlei') sieht den
        // Mandanten-Einladen-Einstieg auch ohne Beta/GA-Flag. Nicht-Kanzlei ⇒
        // OR-Term false ⇒ byte-gleich zu vorher.
        visibility: (ctx) =>
          canUseClientInvite(ctx.uid, asLoopBBetaSellerData(ctx.sellerData)) ||
          isKanzleiAccount(ctx.sellerData)
            ? 'visible'
            : 'hidden',
      },
    ],
  },
  {
    id: 'kanzlei',
    labelKey: 'kanzlei',
    // M-UX-3.2 (`plan-ux-3.2-advisor.md` §2.1/§3): lebt bewusst im
    // OWNER-Baum (E1-Analogie zu `steuerberater`→`advisorPortal`) — die
    // Items sind aber nur sichtbar, wenn `isAdvisorContext` (auch im
    // eigenen Kanzlei-Kontext true). Ein reiner Owner (isAdvisorContext===
    // false) sieht die Sektion nie (beide Items hidden → Sektion gefiltert).
    roles: ['owner'],
    items: [
      {
        // M-UX-7.2 (`plan-ux-7.2-mandanten.md` §3): Landing-Item der
        // Kanzlei-Sektion — „Alle Mandate auf einen Blick".
        // M-ACF-4 (`architecture-advisor-cockpit-first.md` §3.3 / §7.7): das
        // Prädikat wird um das neue `ADVISOR_LANDING_ENABLED`-Flag erweitert,
        // damit mit dem Landing-Flip das Nav-Item für ALLE Advisor sichtbar wird
        // (sonst Orphan-Landing ohne Rückweg) — nicht nur für Loop-B-Beta. Die
        // Geschwister (`advisorCockpit`/`advisorDatev`) bleiben Beta-only.
        // Flag off ⇒ byte-identisch zu heute (nur Beta sieht das Item).
        id: 'mandanten',
        labelKey: 'mandanten',
        route: '/dashboard/mandanten',
        icon: IconClients,
        // M-OB-12 (§8.3): additiv um die W3-Self-Service-Kennzeichnung erweitert.
        // Die bestehende W1/M-ACF-4-Advisor-Kontext-Formel bleibt 1:1 erhalten;
        // zusätzlich sieht eine frische Kanzlei (accountType==='kanzlei') die
        // Mandanten-Landing (Stepper-Einstieg) auch OHNE isAdvisorContext/Mandat.
        // Weiter an `UX_SHELL_ENABLED` geknüpft. Nicht-Kanzlei ⇒ OR-Term false ⇒
        // byte-gleich zu vorher. `advisorCockpit`/`advisorDatev` bleiben
        // UNVERÄNDERT (frische Kanzlei braucht Mandanten-Seite + Invite, kein
        // Cockpit/DATEV).
        visibility: (ctx) =>
          UX_SHELL_ENABLED &&
          ((ctx.isAdvisorContext &&
            (canUseKanzleiCockpit(ctx.uid, asLoopBBetaSellerData(ctx.sellerData)) ||
              ADVISOR_LANDING_ENABLED)) ||
            isKanzleiAccount(ctx.sellerData))
            ? 'visible'
            : 'hidden',
      },
      {
        id: 'advisorCockpit',
        labelKey: 'advisorCockpit',
        route: '/dashboard/cockpit',
        icon: IconPortal,
        visibility: (ctx) =>
          UX_SHELL_ENABLED &&
          ctx.isAdvisorContext &&
          canUseKanzleiCockpit(ctx.uid, asLoopBBetaSellerData(ctx.sellerData))
            ? 'visible'
            : 'hidden',
      },
      {
        id: 'advisorDatev',
        labelKey: 'advisorDatev',
        route: '/dashboard/datev-sammelexport',
        icon: IconCsv,
        visibility: (ctx) =>
          UX_SHELL_ENABLED &&
          ctx.isAdvisorContext &&
          canUseAdvisorDatev(ctx.uid, asLoopBBetaSellerData(ctx.sellerData))
            ? 'visible'
            : 'hidden',
      },
    ],
  },
  {
    id: 'konto',
    labelKey: 'account',
    // M-UX-2.1: schlanker — `advisorPortal`/`clientInvitePortal` sind in die
    // neue `steuerberater`-Sektion umgezogen (§1.7/§9).
    roles: ['owner', 'advisor'],
    items: [
      {
        id: 'profile',
        labelKey: 'profile',
        route: '/dashboard/konto',
        icon: IconUser,
        visibility: () => 'visible',
      },
      {
        id: 'pdfDesign',
        labelKey: 'pdfDesign',
        route: '/dashboard/pdf-design',
        icon: IconTheme,
        visibility: () => 'visible',
      },
      {
        id: 'subscriptionUpgrade',
        labelKey: 'subscribePremium',
        route: '/dashboard/abonnement',
        icon: IconBilling,
        // Heute nur mobil (MainLayout.tsx:941-949) — s. Modul-Kopf zum
        // Desktop-Kombi-Button (`handleManageSubscription`), der bewusst NICHT
        // als NavItem geführt wird (kein statisches Routen-Ziel).
        visibility: (ctx) => (ctx.isPremium ? 'hidden' : 'visible'),
      },
    ],
  },
];

/**
 * „+ Neu"-Schnellerstellungs-Menü — inhaltlich deckungsgleich mit der
 * `actions`-Sektion aus `NAV_TREE` (heutiger Stand: nur Rechnung/Angebot;
 * `scanReceipt` kommt laut §2.1-SOLL-Tabelle erst mit einer späteren Welle
 * dazu, wenn der Scanner-Eintrag ins „+ Neu"-Menü wandert — heute liegt er im
 * Dokumente-Dropdown). Separat von `NAV_TREE` geführt, weil `NAV_ACTIONS`
 * auch außerhalb der Nav-Sektion wiederverwendet wird (Cockpit-Quick-Actions,
 * §5 Kachel 9, ab W4).
 */
export const NAV_ACTIONS: NavAction[] = [
  {
    id: 'createInvoice',
    labelKey: 'createInvoice',
    // architecture-cockpit-route-split.md §4 #2: konstantes Formular-Ziel
    // (`INVOICE_FORM_ROUTE`), auch für die Cockpit-Quick-Action + Mobile-FAB-
    // Sheet. Kein `activeAlsoWhen` — NAV_ACTIONS-Consumer highlighten nicht.
    route: INVOICE_FORM_ROUTE,
    icon: IconInvoice,
    visibility: (ctx) => (ctx.isAdvisorViewingClient ? 'hidden' : 'visible'),
  },
  {
    id: 'createOffer',
    labelKey: 'createOffer',
    route: '/dashboard/angebote',
    icon: IconOffer,
    visibility: (ctx) => (ctx.isAdvisorViewingClient ? 'hidden' : 'visible'),
  },
  {
    // M-UX-6.1 (`plan-ux-6.1-mobile-tabs.md` §4/E4): dritter Quick-Action-Eintrag
    // — Belegscan, der mobilste Use-Case (FAB-Sheet). ADDITIV: erscheint dadurch
    // bewusst AUCH in den Cockpit-Quick-Actions (`CockpitPage.tsx` filtert
    // `visibility(ctx)==='visible'`) — gewollt und flip-ready (§2.1-SOLL listet
    // `scanReceipt` im actions-Kopf; `CockpitPage.test.tsx` mockt NAV_ACTIONS als
    // Fixture ⇒ bleibt grün). `NAV_TREE.actions` bleibt unverändert (kein
    // Scanner-Move — der ist ein eigenes, prod-sichtbares Nav-Paket).
    id: 'scanReceipt',
    // NEUER Key „Beleg scannen" (Verb-Form) — das bestehende `receiptScan`
    // = „Belegscanner" (Substantiv) wird bewusst NICHT reused (§4).
    labelKey: 'scanReceipt',
    // Registrierter Pfad OHNE Query-String: der Unified-Inbox-Redirect
    // (`routes/dashboardChildren.tsx`) übernimmt bei Flag ON; ein `?stream=scan`
    // würde den Registry⊆Paths-Vergleich in `navigation.test.ts` brechen (§4).
    route: '/dashboard/belege',
    icon: IconScan,
    // Bewusst `visible|hidden`, kein `locked` (beide NAV_ACTIONS-Consumer filtern
    // auf `'visible'`; Scan-Upsell im FAB = markiertes Growth-Follow-up).
    visibility: (ctx) =>
      ctx.isAdvisorViewingClient
        ? 'hidden'
        : canUseReceiptScan(ctx.uid, ctx.tier)
          ? 'visible'
          : 'hidden',
  },
];

/**
 * Section-Icon-Zuordnung (Renderer-Hilfe, M-UX-1.2) — nicht Teil der
 * Kern-Interfaces aus §4.1, additiv. M-UX-2.1: vollständig neu befüllt für die
 * sechs neuen Mittel-Band-Sektionen (`plan-ux-2.1-nav-sektionen.md` §1); die
 * alten Keys `documents`/`management` entfallen (Sektionen existieren nicht
 * mehr). `hasEntryForEverySectionId` (`navigation.test.ts`) erzwingt
 * Vollständigkeit gegen `NAV_TREE`.
 */
export const NAV_SECTION_ICONS: Record<string, ComponentType> = {
  uebersicht: IconOverview,
  actions: IconNew,
  pruefung: IconWorklist,
  verkauf: IconInvoice,
  eingaenge: IconInbox,
  zahlungen: IconBank,
  steuer: IconVat,
  stammdaten: IconClients,
  steuerberater: IconAdvisor,
  kanzlei: IconPortal,
  konto: IconUser,
};

/**
 * Reine Baum-Auflösung für einen Rollen-Kontext: filtert Sektionen, deren
 * `roles` die übergebene Rolle nicht enthält, filtert innerhalb der
 * verbleibenden Sektionen `hidden`-Items heraus (§4.1: „filtert hidden, behält
 * locked") und entfernt danach leer gewordene Sektionen. Reine Funktion — kein
 * State, keine Hooks.
 *
 * @param ctx - der aktuelle Nav-Kontext (aus Hooks zusammengebaut, s. `NavContext`).
 * @param role - `isAdvisorViewingClient ? 'advisor' : 'owner'` (§2.2).
 * @returns die für diese Rolle sichtbaren Sektionen mit gefilterten Items.
 */
export function resolveVisibleTree(ctx: NavContext, role: NavRole): NavSection[] {
  return NAV_TREE.filter((section) => section.roles.includes(role))
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => item.visibility(ctx) !== 'hidden'),
    }))
    .filter((section) => section.items.length > 0);
}
